if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.requestVersion = 0;
        this.dismissed = false;
        this.focusElement = this.input;
        this.searchContent = this.querySelector('.search__content');
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.isHeaderSearch = this.closest('header') !== null;

        this.states = {
          OPEN: 'predictive-search-open',
          LOADING: 'btn--loading',
          SEARCH_OPEN: 'search-open'
        };
      }

      connectedCallback() {
        // The header and the search page re-render in the theme editor: the document listeners must go with them.
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.resetButton.addEventListener('click', this.clear.bind(this), { signal });
        this.input.addEventListener('input', () => {
          this.cancelSearchRequest();
          this.dismissed = false;
          this.clearSearchError();
          this.removeAttribute('results');
          const status = this.querySelector('[data-search-status]');
          if (status) status.textContent = '';
          if (!this.getQuery()) {
            this.clear();
            return;
          }
          this.changeTimer = setTimeout(() => {
            this.changeTimer = null;
            this.onChange();
          }, 300);
        }, { signal });
        this.addEventListener('keydown', this.onKeydown.bind(this), { signal });
        this.addEventListener('focusout', (event) => {
          const colourDialog = event.relatedTarget?.closest?.('dialog.mm-colour-dialog[open]');
          if (event.relatedTarget && !this.contains(event.relatedTarget) && !colourDialog) this.toggleSearchState(false);
        }, { signal });
        this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
        document.addEventListener('click', this.handleClickOutside.bind(this), { signal });
        this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });

        document.addEventListener(
          'menu-drawer:open',
          () => {
            this.toggleSearchState(false);
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.dismissed = true;
        this.cancelSearchRequest();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get searchProductTypes() {
        return this.querySelector('.search__types select');
      }

      get form() {
        return this.querySelector('form');
      }

      get headerCollapseOnScroll() {
        return this.header.dataset.collapseOnScroll === 'true';
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains(this.states.OPEN)) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSearchState(false);
          this.focusSearchInput();
        } else if (event.key === 'ArrowDown' && event.target === this.input && this.classList.contains(this.states.OPEN)) {
          const next = Array.from(this.searchContent.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled])'))
            .find((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }
      }

      onFocus(event) {
        if (this.restoringInputFocus) return;
        this.dismissed = false;
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
        }

        if (!this.searchRecommendationEmpty) {
          this.searchContent.classList.remove('hidden');
        }
        this.classList.add('predictive-search-open');
        if (this.getQuery().length === 0) {
          if (this.searchRecommendationEmpty) {
            this.searchContent.classList.add('hidden');
          }
          return;
        }
        const url = this.setupURL().toString();
        // Reopening shows the results of a query the buyer already searched: not a search update.
        this.renderSection(url, { isReopen: true });
      }

      getQuery() {
        return this.input.value.trim();
      }

      clear(event = null) {
        event?.preventDefault();
        this.input.value = '';
        this.removeAttribute('results');
        this.clearSearchError();
        this.toggleSearchState(false);
        this.focusSearchInput();
      }

      handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
          const url = this.setupURL().toString();
          this.renderSection(url);
        }
      }

      onSubmit(event) {
        const productType = this.searchProductTypes?.value;
        if (!productType) return;
        const query = this.getQuery();
        if (query.length === 0) return;
        if (query.startsWith('product_type:')) return;
        event.preventDefault();
        const params = new URLSearchParams();
        params.set('q', `product_type:${productType} AND ${query}`);
        params.set('options[prefix]', 'last');
        window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
      }

      setupURL() {
        const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
        let search_term = this.getQuery();
        if (this.searchProductTypes && this.searchProductTypes.value != '') {
          search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
        }
        return (
          url.searchParams.set('q', search_term),
          url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
          url.searchParams.set('resources[limit_scope]', 'each'),
          url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
          url
        );
      }

      onChange() {
        if (this.dismissed) return;
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }
        const url = this.setupURL().toString();
        this.renderSection(url);
      }

      cancelSearchRequest() {
        clearTimeout(this.changeTimer);
        this.changeTimer = null;
        this.requestVersion += 1;
        const request = this.activeRequest;
        this.activeRequest = null;
        request?.controller.abort();
        if (request) this.settleSearchRequest(request, 'reject', new DOMException('Search cancelled', 'AbortError'));
        this.setLoadingState(false);
      }

      settleSearchRequest(request, method, value) {
        if (request.settled) return;
        request.settled = true;
        clearTimeout(request.timer);
        if (this.activeRequest === request) this.setLoadingState(false);
        try {
          request.event?.[method](value);
        } catch (error) {
          console.error(error);
        }
      }

      isCurrentSearchRequest(request) {
        return this.activeRequest === request && !request.settled && this.isConnected && !this.dismissed &&
          request.version === this.requestVersion && request.query === this.getQuery();
      }

      focusSearchInput() {
        this.restoringInputFocus = true;
        try {
          this.input.focus({ preventScroll: true });
        } finally {
          this.restoringInputFocus = false;
        }
      }

      clearSearchError() {
        this.removeAttribute('error');
        const notice = this.querySelector('[data-search-error]');
        if (notice) notice.hidden = true;
      }

      failSearchRequest(request, error) {
        if (!this.isCurrentSearchRequest(request)) return;
        this.settleSearchRequest(request, 'reject', error);
        this.setLoadingState(false);
        this.removeAttribute('results');
        this.setAttribute('error', 'Failed to load data');
        const status = this.querySelector('[data-search-status]');
        if (status) status.textContent = '';
        const notice = this.querySelector('[data-search-error]');
        const fallback = this.querySelector('[data-search-fallback]');
        if (notice && fallback && this.form) {
          const url = new URL(this.form.getAttribute('action'), window.location.href);
          this.form.querySelectorAll('input[type="hidden"][name]').forEach((field) => url.searchParams.set(field.name, field.value));
          let query = request.query;
          const type = this.searchProductTypes?.value;
          if (type && !query.startsWith('product_type:')) query = `product_type:${type} AND ${query}`;
          url.searchParams.set('q', query);
          fallback.href = url.toString();
          notice.hidden = false;
        }
        this.searchContent?.classList.remove('hidden');
        this.classList.add(this.states.OPEN);
        if (this.isHeaderSearch) document.body.classList.add(this.states.SEARCH_OPEN);
      }

      renderSection(url, { isReopen = false } = {}) {
        this.cancelSearchRequest();
        if (this.dismissed || !this.isConnected || !this.getQuery()) return;
        this.clearSearchError();
        this.removeAttribute('results');
        const request = {
          url, query: this.getQuery(), version: this.requestVersion,
          controller: new AbortController(), settled: false,
          event: isReopen ? null : FoxTheme.storefrontEvents?.start(this, 'SearchUpdateEvent', {
            search: { query: this.getQuery() }
          })
        };
        if (request.version !== this.requestVersion || request.query !== this.getQuery() || !this.isConnected || this.dismissed) {
          this.settleSearchRequest(request, 'reject', new DOMException('Search superseded', 'AbortError'));
          return;
        }
        this.activeRequest = request;
        if (this.cachedMap.has(url)) {
          try {
            this.commitSearchResponse(request, this.cachedMap.get(url));
          } catch (error) {
            this.cachedMap.delete(url);
            this.failSearchRequest(request, error);
          }
          return;
        }
        this.setLoadingState(true);
        request.timer = setTimeout(() => {
          if (request.settled) return;
          request.controller.abort();
          if (this.isCurrentSearchRequest(request)) this.failSearchRequest(request, new Error('Search request timed out'));
          else this.settleSearchRequest(request, 'reject', new DOMException('Search superseded', 'AbortError'));
        }, 15000);
        this.renderSectionFromFetch(request);
      }

      async renderSectionFromFetch(request) {
        try {
          const response = await fetch(request.url, { signal: request.controller.signal });
          if (!response.ok) throw new Error('Search request failed');
          const text = await response.text();
          if (!this.isCurrentSearchRequest(request)) {
            this.settleSearchRequest(request, 'reject', new DOMException('Search superseded', 'AbortError'));
            return;
          }
          this.commitSearchResponse(request, text);
        } catch (error) {
          if (this.isCurrentSearchRequest(request)) this.failSearchRequest(request, error);
          else this.settleSearchRequest(request, 'reject', new DOMException('Search superseded', 'AbortError'));
        }
      }

      commitSearchResponse(request, text) {
        if (!this.isCurrentSearchRequest(request)) {
          this.settleSearchRequest(request, 'reject', new DOMException('Search superseded', 'AbortError'));
          return;
        }
        const result = this.renderSearchResults(text);
        this.cachedMap.delete(request.url);
        this.cachedMap.set(request.url, text);
        while (this.cachedMap.size > 32) this.cachedMap.delete(this.cachedMap.keys().next().value);
        this.setLoadingState(false);
        this.settleSearchRequest(request, 'resolve', { totalCount: result.count });
        if (result.focusLost) {
          try {
            this.focusSearchInput();
          } catch (error) {
            console.error(error);
          }
        }
      }

      getTotalResults() {
        const results = document.getElementById(`PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`);
        return Number(results?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      }
      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const target = document.getElementById(id);
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const content = parsed.getElementById(id);
        const marker = content?.querySelector('[data-total-results]');
        const rawCount = marker?.dataset.totalResults;
        if (!target || !content || !/^\d+$/.test(rawCount || '')) throw new Error('Invalid search section');
        const count = Number(rawCount);
        if (!Number.isSafeInteger(count)) throw new Error('Invalid search count');
        const previousFocus = document.activeElement;
        const hadFocus = target.contains(previousFocus);
        target.innerHTML = content.innerHTML;
        this.clearSearchError();
        const status = this.querySelector('[data-search-status]');
        if (status) status.textContent = marker.textContent || '';
        this.searchContent?.classList.remove('hidden');
        this.setAttribute('results', 'true');
        this.classList.add(this.states.OPEN);
        if (this.isHeaderSearch) {
          document.body.classList.add(this.states.SEARCH_OPEN);
          this.toggleNavButtonVisibility(true);
        }
        return { count, focusLost: hadFocus && !previousFocus.isConnected && document.activeElement === document.body };
      }

      handleClickOutside(event) {
        const target = event.target;
        if (target.closest?.('dialog.mm-colour-dialog')) return;
        const shouldClose = this.isHeaderSearch
          ? !this.contains(target) &&
            ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
              target.classList.contains('header__search-close'))
          : !this.contains(target);

        if (shouldClose) {
          setTimeout(() => this.toggleSearchState(false));
        }
      }

      toggleSearchState(isOpen) {
        this.dismissed = !isOpen;
        if (!isOpen) {
          this.cancelSearchRequest();
          const status = this.querySelector('[data-search-status]');
          if (status) status.textContent = '';
        }
        this.classList.toggle(this.states.OPEN, isOpen);
        if (this.isHeaderSearch) {
          document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
          this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen && this.searchRecommendationEmpty) {
          this.searchContent?.classList.add('hidden');
        }
      }

      toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
          this.headerCollapseOnScroll &&
          this.toggleNavigationButton.classList.toggle('is-show', isOpen);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.setAttribute('loading', 'true');
          this.resetButton.classList.add(this.states.LOADING);
        } else {
          this.removeAttribute('loading');
          this.resetButton.classList.remove(this.states.LOADING);
        }
      }
    }
  );
}
